import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';

import { ActiveChannelTypeEnum } from 'constants-channel';

import { bulkParentChannelsQuery } from 'graphql-queries';

import type { ChannelListItem } from './useBulkChannelSelection';

const PARENT_CHANNEL_PAGE_SIZE = 25;

type BulkParentChannelsResponse = {
  bulkModuleChannels?: {
    pageInfo?: { total?: number };
    items?: Array<{
      _id: string;
      name: string;
      slug: string;
      type: string;
    }>;
  };
};

function channelTypeFromApi(value: string): ActiveChannelTypeEnum {
  const values = Object.values(ActiveChannelTypeEnum);
  for (let i = 0; i < values.length; i++) {
    const candidate = values[i];
    if (candidate === value) {
      return candidate;
    }
  }
  // Rare legacy / unexpected types — keep picker usable without casting.
  return ActiveChannelTypeEnum.Company;
}

/**
 * Loads top-level channels (`parentId` IS NULL, non-subchannel) for the bulk
 * module tool parent picker, with optional name filter.
 *
 * Uses the admin-only `bulkModuleChannels` query with `parent: null` so root
 * channels are honored server-side. Super-users receive all matching channels.
 */
export function useParentChannelSearch() {
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [keyword]);

  const trimmedKeyword = keyword.trim();

  const { data, loading } = useQuery<BulkParentChannelsResponse>(
    bulkParentChannelsQuery,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        pagination: {
          start: page * PARENT_CHANNEL_PAGE_SIZE,
          perPage: PARENT_CHANNEL_PAGE_SIZE,
        },
        search: {
          isSub: false,
          parent: null,
          ...(trimmedKeyword.length > 0
            ? { name: { type: 'like', value: trimmedKeyword } }
            : {}),
        },
      },
    }
  );

  const channels: ChannelListItem[] = useMemo(() => {
    const rawItems = data?.bulkModuleChannels?.items ?? [];

    return rawItems.map(item => ({
      _id: item._id,
      name: item.name,
      slug: item.slug,
      type: channelTypeFromApi(item.type),
    }));
  }, [data?.bulkModuleChannels?.items]);

  const total = data?.bulkModuleChannels?.pageInfo?.total ?? 0;

  return {
    channels,
    loading,
    keyword,
    setKeyword,
    total,
    page,
    setPage,
    pageSize: PARENT_CHANNEL_PAGE_SIZE,
  };
}
